<div class="flex flex-col gap-8">
  <app-analytics-filters
    [professionals]="professionals()"
    [services]="services()"
    [canFilterProfessional]="canSeeTeam"
    (filtersChanged)="onFiltersChanged($event)">
    <!-- Descargar -->
    <div class="relative">
      <button type="button" (click)="toggleExportMenu($event)"
        [disabled]="!analytics() || !!exporting()"
        [attr.aria-expanded]="exportMenuOpen()"
        aria-haspopup="menu"
        class="inline-flex h-10 items-center gap-2 rounded-xl bg-gray-900 px-4 text-sm font-medium text-white transition-colors hover:bg-gray-800 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-500 disabled:opacity-50 dark:bg-white dark:text-gray-900 dark:hover:bg-gray-100">
        <svg class="h-4 w-4" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24" aria-hidden="true">
          <path stroke-linecap="round" stroke-linejoin="round" d="M12 4v11m0 0-4-4m4 4 4-4M5 19h14" />
        </svg>
        {{ exporting() ? 'Generando…' : 'Descargar informe' }}
      </button>
      @if (exportMenuOpen()) {
        <div role="menu" (click)="$event.stopPropagation()"
          class="absolute right-0 z-30 mt-2 w-72 overflow-hidden rounded-xl border border-gray-200 bg-white p-1.5 shadow-theme-lg dark:border-gray-800 dark:bg-gray-dark">
          <button type="button" role="menuitem" (click)="export('xlsx')"
            class="flex w-full items-start gap-3 rounded-lg px-3 py-2.5 text-left hover:bg-gray-50 focus-visible:bg-gray-50 focus-visible:outline-none dark:hover:bg-white/5 dark:focus-visible:bg-white/5">
            <span class="mt-0.5 inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-success-50 text-xs font-bold text-success-700 dark:bg-success-500/15 dark:text-success-400">XLS</span>
            <span>
              <span class="block text-sm font-medium text-gray-900 dark:text-white">Excel</span>
              <span class="block text-sm text-gray-500 dark:text-gray-400">Resumen y todas las citas, listo para filtrar y sumar.</span>
            </span>
          </button>
          <button type="button" role="menuitem" (click)="export('pdf')"
            class="flex w-full items-start gap-3 rounded-lg px-3 py-2.5 text-left hover:bg-gray-50 focus-visible:bg-gray-50 focus-visible:outline-none dark:hover:bg-white/5 dark:focus-visible:bg-white/5">
            <span class="mt-0.5 inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-error-50 text-xs font-bold text-error-700 dark:bg-error-500/15 dark:text-error-400">PDF</span>
            <span>
              <span class="block text-sm font-medium text-gray-900 dark:text-white">PDF</span>
              <span class="block text-sm text-gray-500 dark:text-gray-400">Informe para compartir o imprimir, con el detalle al final.</span>
            </span>
          </button>
        </div>
      }
    </div>
  </app-analytics-filters>

  @if (loadError()) {
    <div class="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-error-200 bg-error-50 px-5 py-4 text-sm text-error-700 dark:border-error-500/30 dark:bg-error-500/10 dark:text-error-300" role="alert">
      <span>No se pudieron cargar los datos del informe. Revisa tu conexión e intenta de nuevo.</span>
      <button type="button" (click)="loadAnalytics()" class="rounded-lg bg-white px-3 py-1.5 font-medium text-error-700 shadow-theme-xs hover:bg-error-25 dark:bg-gray-900 dark:text-error-300">Reintentar</button>
    </div>
  }

  <app-analytics-summary [data]="analytics()" [loading]="loading()" [rangeLabel]="filterState()?.rangeLabel ?? ''" (openSummary)="openSummary()" />

  @if (analytics(); as data) {
    <div class="grid grid-cols-1 gap-6 transition-opacity lg:grid-cols-3" [class.opacity-60]="loading()">
      <app-report-panel class="lg:col-span-2" title="Ingresos cobrados" description="Pagos recibidos en el período, sin importar la fecha de la cita.">
        <app-revenue-chart [data]="data.charts.revenueOverTime" [rangeStart]="filterState()!.rangeStart" [rangeEnd]="filterState()!.rangeEnd" />
      </app-report-panel>

      <app-report-panel title="Cómo terminaron las citas" description="Estado actual de las citas agendadas.">
        <app-status-breakdown [data]="data.charts.appointmentsByStatus" [attendanceRate]="data.kpis.attendanceRate" />
      </app-report-panel>

      <app-report-panel title="Servicios" description="Valor de las citas completadas o pagadas.">
        <div panelActions class="flex rounded-lg bg-gray-100 p-0.5 dark:bg-gray-800" role="group" aria-label="Ordenar servicios por">
          <button type="button" (click)="serviceMode.set('revenue')" [attr.aria-pressed]="serviceMode() === 'revenue'"
            class="rounded-md px-2.5 py-1 text-xs font-medium"
            [class]="serviceMode() === 'revenue' ? 'bg-white text-gray-900 shadow-theme-xs dark:bg-gray-950 dark:text-white' : 'text-gray-500 dark:text-gray-400'">Valor</button>
          <button type="button" (click)="serviceMode.set('count')" [attr.aria-pressed]="serviceMode() === 'count'"
            class="rounded-md px-2.5 py-1 text-xs font-medium"
            [class]="serviceMode() === 'count' ? 'bg-white text-gray-900 shadow-theme-xs dark:bg-gray-950 dark:text-white' : 'text-gray-500 dark:text-gray-400'">Citas</button>
        </div>
        <app-ranked-list [rows]="serviceRows()" emptyText="No hay citas en estas fechas." />
      </app-report-panel>

      @if (canSeeTeam) {
        <app-report-panel title="Equipo" description="Valor generado por cada profesional.">
          <app-ranked-list [rows]="teamRows()" emptyText="Ninguna cita tiene profesional asignado." />
        </app-report-panel>
      }

      <app-report-panel title="Cómo te pagan" description="Lo cobrado según el método de pago.">
        <app-ranked-list [rows]="paymentRows()" emptyText="No hay pagos registrados en estas fechas." />
      </app-report-panel>

      <app-report-panel title="Clientes" description="Quién vino y cuánto valor dejó.">
        @if (clientSplit(); as split) {
          @if (split.total > 0) {
            <div class="mb-5">
              <div class="flex items-baseline justify-between text-sm">
                <span class="text-gray-700 dark:text-gray-300"><span class="font-semibold text-gray-900 tabular-nums dark:text-white">{{ split.newCount }}</span> nuevos</span>
                <span class="text-gray-700 dark:text-gray-300"><span class="font-semibold text-gray-900 tabular-nums dark:text-white">{{ split.returningCount }}</span> recurrentes</span>
              </div>
              <div class="mt-2 flex h-2.5 gap-0.5 overflow-hidden rounded-full bg-gray-100 dark:bg-gray-800" role="img"
                [attr.aria-label]="split.newCount + ' clientes nuevos y ' + split.returningCount + ' recurrentes'">
                <div class="h-full rounded-l-full bg-brand-500" [style.width.%]="split.newShare"></div>
                <div class="h-full flex-1 rounded-r-full bg-brand-200 dark:bg-brand-500/40"></div>
              </div>
            </div>
          }
        }
        <app-ranked-list [rows]="clientRows()" [limit]="5" emptyText="No hay clientes con citas en estas fechas." />
      </app-report-panel>

      <app-report-panel [class]="canSeeTeam ? 'lg:col-span-2' : 'lg:col-span-3'" title="Horarios con más citas" description="Citas no canceladas por día y hora de inicio.">
        <app-busy-hours [data]="data.charts.appointmentsByWeekdayHour" />
      </app-report-panel>
    </div>

    <app-report-panel title="Citas del período" description="El mismo detalle que se incluye en el Excel y al final del PDF.">
      <app-appointments-table [request]="filterState()!.request" [showProfessional]="canSeeTeam" />
    </app-report-panel>
  }
</div>

<!-- Resumen flotante: siempre a mano mientras se recorre el informe -->
@if (analytics() && !summaryOpen()) {
  <button type="button" (click)="openSummary()"
    class="fixed right-4 z-50 inline-flex items-center gap-2 rounded-full bg-gray-900 py-2.5 pl-3.5 pr-4 text-sm font-medium text-white shadow-theme-lg transition-colors hover:bg-gray-800 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-500 sm:right-6 dark:bg-white dark:text-gray-900 dark:hover:bg-gray-100"
    [class]="liftFab() ? 'bottom-20' : 'bottom-4 sm:bottom-6'"
    aria-haspopup="dialog">
    <svg class="h-4 w-4" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" viewBox="0 0 24 24" aria-hidden="true">
      <path d="M4 6h16M4 12h10M4 18h7" />
    </svg>
    Resumen del período
  </button>
}

<app-summary-drawer
  [open]="summaryOpen()"
  [data]="analytics()"
  [rangeLabel]="filterState()?.rangeLabel ?? ''"
  [businessName]="businessName"
  [canDownload]="!exporting()"
  (closed)="closeSummary()"
  (downloadPdf)="downloadPdfFromSummary()" />
